import { uiSurface } from "shared/ui/uiSurface"
import { Save } from "@mui/icons-material"
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  Switch,
  Typography,
} from "@mui/material"
import { FC, useCallback, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  useCreateConfigurationMutation,
  useGetConfigurationsQuery,
  useUpdateConfigurationMutation,
} from "../../apis/appApi"

type ManifestFormState = {
  MANIFEST_ENABLED: string
  MANIFEST_LABEL_FORMAT: string
  MANIFEST_INCLUDE_NOTES: string
  MANIFEST_INCLUDE_AMOUNT: string
  MANIFEST_INCLUDE_BARCODE: string
  MANIFEST_BARCODE_TYPE: string
  MANIFEST_INCLUDE_SIGNATURE_LINE: string
}

const DEFAULTS: ManifestFormState = {
  MANIFEST_ENABLED: "false",
  MANIFEST_LABEL_FORMAT: "A4",
  MANIFEST_INCLUDE_NOTES: "false",
  MANIFEST_INCLUDE_AMOUNT: "false",
  MANIFEST_INCLUDE_BARCODE: "true",
  MANIFEST_BARCODE_TYPE: "CODE128",
  MANIFEST_INCLUDE_SIGNATURE_LINE: "false",
}

const MANIFEST_KEYS = Object.keys(DEFAULTS) as (keyof ManifestFormState)[]

export const ManifestTab: FC = () => {
  const { t } = useTranslation()
  const { data: configurations, isLoading } = useGetConfigurationsQuery()
  const [createConfiguration] = useCreateConfigurationMutation()
  const [updateConfiguration] = useUpdateConfigurationMutation()

  const [form, setForm] = useState<ManifestFormState>(DEFAULTS)
  const [snackbar, setSnackbar] = useState<{
    open: boolean
    severity: "success" | "error"
    message: string
  }>({ open: false, severity: "success", message: "" })
  const [saving, setSaving] = useState(false)

  // Build a map of existing configs keyed by name
  const configMap = useMemo(() => {
    const map: Record<string, { id: number; value: string }> = {}
    if (configurations) {
      for (const cfg of configurations) {
        if (MANIFEST_KEYS.includes(cfg.name as keyof ManifestFormState)) {
          map[cfg.name] = { id: cfg.id, value: cfg.value ?? "" }
        }
      }
    }
    return map
  }, [configurations])

  // Load values from configs on mount / data change
  const loadedValues = useMemo(() => {
    const values = { ...DEFAULTS }
    for (const key of MANIFEST_KEYS) {
      if (configMap[key]) {
        values[key] = configMap[key].value
      }
    }
    return values
  }, [configMap])

  useEffect(() => {
    setForm(loadedValues)
  }, [loadedValues])

  const hasChanges = useMemo(
    () => MANIFEST_KEYS.some((key) => form[key] !== loadedValues[key]),
    [form, loadedValues],
  )

  const setField = useCallback(
    (key: keyof ManifestFormState, value: string) => {
      setForm((prev) => ({ ...prev, [key]: value }))
    },
    [],
  )

  const toggleField = useCallback((key: keyof ManifestFormState) => {
    setForm((prev) => ({
      ...prev,
      [key]: prev[key] === "true" ? "false" : "true",
    }))
  }, [])

  const handleSave = async () => {
    setSaving(true)
    try {
      for (const key of MANIFEST_KEYS) {
        const existing = configMap[key]
        if (existing) {
          await updateConfiguration({
            id: existing.id,
            body: { value: form[key] },
          })
        } else {
          await createConfiguration({
            name: key,
            value: form[key],
            enabled: true,
          })
        }
      }
      setSnackbar({
        open: true,
        severity: "success",
        message: t("configuration.manifest.saved"),
      })
    } catch {
      setSnackbar({
        open: true,
        severity: "error",
        message: t("configuration.manifest.saveError"),
      })
    } finally {
      setSaving(false)
    }
  }

  if (isLoading) {
    return (
      <Box
        data-testid="manifest-loading"
        sx={{ display: "flex", justifyContent: "center", py: 8 }}
      >
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Container
      {...uiSurface("UI-DLV2-182")}
      data-testid="manifest-tab"
      maxWidth="md"
      sx={{ py: 4 }}
    >
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" fontWeight={600} color="text.primary">
          {t("configuration.manifest.title")}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {t("configuration.manifest.subtitle")}
        </Typography>
      </Box>

      <Stack spacing={3}>
        {/* General Section */}
        <Box>
          <Typography
            variant="subtitle1"
            fontWeight={700}
            color="text.primary"
            sx={{ mb: 2 }}
          >
            {t("configuration.manifest.general")}
          </Typography>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Box>
              <Typography variant="body2" fontWeight={500}>
                {t("configuration.manifest.enabled")}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t("configuration.manifest.enabledDescription")}
              </Typography>
            </Box>
            <Switch
              data-testid="manifest-enabled-switch"
              checked={form.MANIFEST_ENABLED === "true"}
              onChange={() => toggleField("MANIFEST_ENABLED")}
            />
          </Stack>
        </Box>

        <Divider />

        {/* Label Format & Barcode Section */}
        <Box>
          <Typography
            variant="subtitle1"
            fontWeight={700}
            color="text.primary"
            sx={{ mb: 2 }}
          >
            {t("configuration.manifest.labelFormatTitle")}
          </Typography>
          <Stack spacing={3}>
            <FormControl fullWidth size="small">
              <InputLabel>{t("configuration.manifest.labelFormat")}</InputLabel>
              <Select
                data-testid="manifest-label-format-select"
                value={form.MANIFEST_LABEL_FORMAT}
                label={t("configuration.manifest.labelFormat")}
                onChange={(e) =>
                  setField("MANIFEST_LABEL_FORMAT", e.target.value)
                }
              >
                <MenuItem value="A4">
                  {t("configuration.manifest.formatA4")}
                </MenuItem>
                <MenuItem value="THERMAL_4X6">
                  {t("configuration.manifest.formatThermal")}
                </MenuItem>
              </Select>
            </FormControl>

            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography variant="body2" fontWeight={500}>
                {t("configuration.manifest.includeBarcode")}
              </Typography>
              <Switch
                data-testid="manifest-include-barcode-switch"
                checked={form.MANIFEST_INCLUDE_BARCODE === "true"}
                onChange={() => toggleField("MANIFEST_INCLUDE_BARCODE")}
              />
            </Stack>

            {form.MANIFEST_INCLUDE_BARCODE === "true" && (
              <FormControl fullWidth size="small">
                <InputLabel>
                  {t("configuration.manifest.barcodeType")}
                </InputLabel>
                <Select
                  data-testid="manifest-barcode-type-select"
                  value={form.MANIFEST_BARCODE_TYPE}
                  label={t("configuration.manifest.barcodeType")}
                  onChange={(e) =>
                    setField("MANIFEST_BARCODE_TYPE", e.target.value)
                  }
                >
                  <MenuItem value="CODE128">CODE128</MenuItem>
                  <MenuItem value="QR">QR</MenuItem>
                </Select>
              </FormControl>
            )}
          </Stack>
        </Box>

        <Divider />

        {/* Content Section */}
        <Box>
          <Typography
            variant="subtitle1"
            fontWeight={700}
            color="text.primary"
            sx={{ mb: 2 }}
          >
            {t("configuration.manifest.content")}
          </Typography>
          <Stack spacing={1}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Box>
                <Typography variant="body2" fontWeight={500}>
                  {t("configuration.manifest.includeNotes")}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t("configuration.manifest.includeNotesDescription")}
                </Typography>
              </Box>
              <Switch
                data-testid="manifest-include-notes-switch"
                checked={form.MANIFEST_INCLUDE_NOTES === "true"}
                onChange={() => toggleField("MANIFEST_INCLUDE_NOTES")}
              />
            </Stack>

            <Divider sx={{ my: 1 }} />

            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Box>
                <Typography variant="body2" fontWeight={500}>
                  {t("configuration.manifest.includeAmount")}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t("configuration.manifest.includeAmountDescription")}
                </Typography>
              </Box>
              <Switch
                data-testid="manifest-include-amount-switch"
                checked={form.MANIFEST_INCLUDE_AMOUNT === "true"}
                onChange={() => toggleField("MANIFEST_INCLUDE_AMOUNT")}
              />
            </Stack>

            <Divider sx={{ my: 1 }} />

            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Box>
                <Typography variant="body2" fontWeight={500}>
                  {t("configuration.manifest.includeSignatureLine")}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t("configuration.manifest.includeSignatureLineDescription")}
                </Typography>
              </Box>
              <Switch
                data-testid="manifest-include-signature-switch"
                checked={form.MANIFEST_INCLUDE_SIGNATURE_LINE === "true"}
                onChange={() => toggleField("MANIFEST_INCLUDE_SIGNATURE_LINE")}
              />
            </Stack>
          </Stack>
        </Box>

        <Divider />

        {/* Save Button */}
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            data-testid="manifest-save-btn"
            variant="contained"
            startIcon={saving ? <CircularProgress size={16} /> : <Save />}
            onClick={handleSave}
            disabled={!hasChanges || saving}
            sx={{ borderRadius: 2, textTransform: "none", px: 4 }}
          >
            {t("save")}
          </Button>
        </Box>
      </Stack>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  )
}
